/**
 * Global Rating Event Emitter
 * Broadcasts rating updates across all components
 *
 * Usage:
 * - ratingEvents.emit('rating-updated', { trackId, userRating, averageRating })
 * - ratingEvents.on('rating-updated', callback)
 * - ratingEvents.off('rating-updated', callback)
 */

class RatingEventEmitter {
  constructor () {
    this.listeners = {}
  }

  /**
   * Subscribe to rating update events
   * @param {string} event - Event name (e.g., 'rating-updated')
   * @param {Function} callback - Function to call when event fires
   */
  on (event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)
  }

  /**
   * Unsubscribe from rating update events
   * @param {string} event - Event name
   * @param {Function} callback - Function to remove
   */
  off (event, callback) {
    if (!this.listeners[event]) return
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
  }

  /**
   * Emit a rating update event
   * @param {string} event - Event name
   * @param {Object} data - Event data
   * @param {string} data.trackId - Track ID that was rated
   * @param {number} data.userRating - User's rating (1-5)
   * @param {number} data.averageRating - New average rating (rounded to 0.5)
   * @param {number} data.ratingCount - Total number of ratings
   */
  emit (event, data) {
    if (!this.listeners[event]) return

    console.log(`🔔 Rating event emitted: ${event}`, data)

    this.listeners[event].forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('Error in rating event listener:', error)
      }
    })
  }

  /**
   * Remove all listeners (cleanup)
   */
  clear () {
    this.listeners = {}
  }
}

// Create singleton instance
export const ratingEvents = new RatingEventEmitter()

/**
 * Broadcast that a track's rating has been updated
 * @param {string|number} trackId - Track ID
 * @param {number} userRating - User's rating
 * @param {number} averageRating - New average rating
 * @param {number} ratingCount - Total ratings
 */
export function broadcastRatingUpdate (trackId, userRating, averageRating, ratingCount) {
  ratingEvents.emit('rating-updated', {
    trackId: String(trackId),
    userRating,
    averageRating,
    ratingCount,
    timestamp: Date.now()
  })
}

/**
 * Subscribe to rating updates
 * @param {Function} callback - Called when any rating is updated
 * @returns {Function} Unsubscribe function
 */
export function onRatingUpdate (callback) {
  ratingEvents.on('rating-updated', callback)

  // Return unsubscribe function
  return () => {
    ratingEvents.off('rating-updated', callback)
  }
}

export default ratingEvents
